import { useEffect, useImperativeHandle, forwardRef, useState } from "react";
import { Row, Col } from "react-bootstrap";
import Label from "../hint-controls/Label";
import Textbox from "../hint-controls/Textbox";
import { ComponentHeaderData } from "components/utility-component/wafer-map-widget/wafer-map/web-gl-utils/Types";
import {
  formatLotId,
  formatWaferId,
} from "components/utility-component/wafer-map-widget/wafer-map/web-gl-utils/Utils";

interface MetadataProps {
  componentHeaderData: ComponentHeaderData;
  isEditMode?: boolean;
  onChange?: (newValues: Partial<ComponentHeaderData>) => void;
  className?: string;
}

export interface MetadataHandle {
  validate: () => { [key: string]: string };
}

const Metadata = forwardRef<MetadataHandle, MetadataProps>(
  ({ componentHeaderData, isEditMode = false, onChange, className }, ref) => {
    const [facilityError, setFacilityError] = useState<string>("");

    // Validate metadata fields
    const validateMetadata = (): { [key: string]: string } => {
      const errors: { [key: string]: string } = {};

      // Validate facility field (required)
      if (
        !componentHeaderData.facility ||
        componentHeaderData.facility === ""
      ) {
        errors.facility = "Facility Is Required";
        setFacilityError("Facility Is Required");
      } else {
        setFacilityError("");
      }

      return errors;
    };

    // Expose validate function to parent via ref
    useImperativeHandle(ref, () => ({
      validate: validateMetadata,
    }));
    // Extract metadata fields from componentHeaderData
    const getMetadataValue = (key: 'facility' | 'workCenter' | 'programName' | 'lotId' | 'waferId'): string => {
      return (componentHeaderData[key] as string) || "";
    };

    // Initialize auto-generated fields in Add mode only (Lot ID and Wafer ID)
    useEffect(() => {
      if (!isEditMode) {
        const updates: Partial<ComponentHeaderData> = {};

        // Set Work Center if not already set
        if (!componentHeaderData.workCenter) {
          updates.workCenter = "Device Map";
        }

        updates.lotId = formatLotId();
        updates.waferId = formatWaferId();

        if (Object.keys(updates).length > 0 && onChange) {
          onChange(updates);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on mount

    const handleFacilityChange = (value: string) => {
      if (isEditMode) return; // Don't allow changes in edit mode

      if (onChange) {
        onChange({ facility: value });
      }
    };

    const isReadOnly = isEditMode || false;

    return (
      <div className={`${className || ""}`}>
<Row className="mt10">
  <Col lg={12}>
    <Row>
      <Col>
        <Label 
        labelTitle="Facility" 
        labelPosition="top"
        labelSize="100"
        fieldSize="100"
        errorSize="100"
        required 
        error={facilityError} 
        isFieldTouched>
          <Textbox
            autoComplete="off"
            name="facility"
            placeholder="Facility"
            value={getMetadataValue("facility")}
            readonly={isReadOnly}
            disabled={isReadOnly}
            className={facilityError ? "error-field-outline" : ""}
            style={isReadOnly ? { backgroundColor: "#eee" } : undefined}
            onChange={(e: any) => {
              handleFacilityChange(e.target.value);
              if (facilityError) setFacilityError("");
            }}
          />
        </Label>
      </Col>

      <Col>
        <Label 
        labelTitle="Work Center" 
        labelPosition="top"
        labelSize="100"
        fieldSize="100"
        errorSize="100">
          <Textbox
            name="workCenter"
            value={getMetadataValue("workCenter") || "Device Map"}
            readonly
            disabled
            style={{ backgroundColor: "#eee" }}
          />
        </Label>
      </Col>

      <Col>
        <Label 
        labelTitle="Program Name" 
        labelPosition="top"
        labelSize="100"
        fieldSize="100"
        errorSize="100">
          <Textbox
            autoComplete="off"
            placeholder="Program Name"
            name="programName"
            value={getMetadataValue("programName")}
            readonly={true}
            disabled={true}
            style={{ backgroundColor: "#eee" }}
          />
        </Label>
      </Col>

      <Col>
        <Label labelTitle="Lot ID" 
        labelPosition="top"
        labelSize="100"
        fieldSize="100"
        errorSize="100">
          <Textbox
            name="lotId"
            value={getMetadataValue("lotId")}
            readonly
            disabled
            style={{ backgroundColor: "#eee" }}
          />
        </Label>
      </Col>

      <Col>
        <Label 
        labelTitle="Wafer ID" 
        labelPosition="top"
        labelSize="100"
        fieldSize="100"
        errorSize="100">
          <Textbox
            name="waferId"
            value={getMetadataValue("waferId")}
            readonly
            disabled
            style={{ backgroundColor: "#eee" }}
          />
        </Label>
      </Col>
    </Row>
  </Col>
</Row>

      </div>
    );
  }
);

Metadata.displayName = "Metadata";

export default Metadata;
